import Modal from "components/Elements/Modalv2";
import Button, { ButtonType } from "components/Elements/Buttonv2";
import Input from "components/Elements/Inputv2";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "services/api.service";
import ApiConfig from "constants/api";
import { TemplateType } from "types/Template";
import { defaultInAppState } from "pages/InAppBuilder/InAppBuilder";
import { EMAIL_DEFAULTS, EmailTemplateDefault } from "../defaults/email";
import {
  EMAIL_CONTENT_PRESETS,
  EmailContentPreset,
} from "../defaults/email-content";
import {
  composeEmail,
  composeWithPlainLetter,
  isLetterCompatible,
} from "../defaults/email-compose";
import { IN_APP_DEFAULTS, InAppDefault } from "../defaults/in-app";
import { PUSH_DEFAULTS, PushDefault } from "../defaults/push";
import {
  useFavoriteTemplateStyles,
  FavoriteTemplateStyleType,
} from "hooks/useFavoriteTemplateStyles";
import EmailPreview from "./previews/EmailPreview";
import InAppPreview from "./previews/InAppPreview";
import PushPreview from "./previews/PushPreview";

interface CreateTemplateWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = "basics" | "starting-point";

interface TypeOption {
  key: TemplateType;
  label: string;
  description: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  {
    key: TemplateType.EMAIL,
    label: "Email",
    description: "A subject + body, sent via your email channel.",
  },
  {
    key: TemplateType.SMS,
    label: "SMS",
    description: "A short text message.",
  },
  {
    key: TemplateType.SLACK,
    label: "Slack",
    description: "A message posted to Slack.",
  },
  {
    key: TemplateType.WEBHOOK,
    label: "Webhook",
    description: "An HTTP call out to another system.",
  },
  {
    key: TemplateType.PUSH,
    label: "Push",
    description: "A mobile push notification.",
  },
  {
    key: TemplateType.IN_APP_MESSAGE,
    label: "In-App",
    description: "An in-app modal shown to your users.",
  },
];

const BLANK_KEY = "__blank__";

const typeLabel = (t: TemplateType) =>
  TYPE_OPTIONS.find((o) => o.key === t)?.label.toLowerCase() ?? "template";

const CreateTemplateWizardModal: FC<CreateTemplateWizardModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("basics");
  const [templateType, setTemplateType] = useState<TemplateType>(
    TemplateType.EMAIL
  );
  const [templateName, setTemplateName] = useState("");
  const [selectedDefaultKey, setSelectedDefaultKey] =
    useState<string>(BLANK_KEY);
  const [selectedContentKey, setSelectedContentKey] =
    useState<string>(BLANK_KEY);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { favorites, setFavorite } = useFavoriteTemplateStyles();

  const favoriteTypeForTemplate = (
    t: TemplateType
  ): FavoriteTemplateStyleType | null => {
    if (t === TemplateType.EMAIL) return "email";
    if (t === TemplateType.IN_APP_MESSAGE) return "inApp";
    if (t === TemplateType.PUSH) return "push";
    return null;
  };

  const defaultsForType = (t: TemplateType): { key: string }[] => {
    if (t === TemplateType.EMAIL) return EMAIL_DEFAULTS;
    if (t === TemplateType.IN_APP_MESSAGE) return IN_APP_DEFAULTS;
    if (t === TemplateType.PUSH) return PUSH_DEFAULTS;
    return [];
  };

  const reset = () => {
    setStep("basics");
    setTemplateType(TemplateType.EMAIL);
    setTemplateName("");
    setSelectedDefaultKey(BLANK_KEY);
    setSelectedContentKey(BLANK_KEY);
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    reset();
  }, [isOpen]);

  // Reset starting-point selection any time the type changes. If the user
  // has a favorite for the new type and it still exists in the catalog,
  // start there instead of Blank.
  useEffect(() => {
    const favType = favoriteTypeForTemplate(templateType);
    const favKey = favType ? favorites[favType] : undefined;
    const available = defaultsForType(templateType);
    if (favKey && available.some((d) => d.key === favKey)) {
      setSelectedDefaultKey(favKey);
    } else {
      setSelectedDefaultKey(BLANK_KEY);
    }
    setSelectedContentKey(BLANK_KEY);
  }, [templateType, favorites]);

  // ---- Per-type default libraries ----
  const groupedEmailDefaults = useMemo(() => {
    const map = new Map<string, EmailTemplateDefault[]>();
    for (const d of EMAIL_DEFAULTS) {
      if (!map.has(d.category)) map.set(d.category, []);
      map.get(d.category)!.push(d);
    }
    return Array.from(map.entries());
  }, []);

  const groupedInAppDefaults = useMemo(() => {
    const map = new Map<string, InAppDefault[]>();
    for (const d of IN_APP_DEFAULTS) {
      if (!map.has(d.category)) map.set(d.category, []);
      map.get(d.category)!.push(d);
    }
    return Array.from(map.entries());
  }, []);

  const groupedPushDefaults = useMemo(() => {
    const map = new Map<string, PushDefault[]>();
    for (const d of PUSH_DEFAULTS) {
      if (!map.has(d.category)) map.set(d.category, []);
      map.get(d.category)!.push(d);
    }
    return Array.from(map.entries());
  }, []);

  const selectedEmailDefault = useMemo<EmailTemplateDefault | null>(() => {
    if (templateType !== TemplateType.EMAIL) return null;
    if (selectedDefaultKey === BLANK_KEY) return null;
    return EMAIL_DEFAULTS.find((d) => d.key === selectedDefaultKey) ?? null;
  }, [selectedDefaultKey, templateType]);

  const groupedEmailContent = useMemo(() => {
    const map = new Map<string, EmailContentPreset[]>();
    for (const p of EMAIL_CONTENT_PRESETS) {
      if (!map.has(p.category)) map.set(p.category, []);
      map.get(p.category)!.push(p);
    }
    return Array.from(map.entries());
  }, []);

  const selectedEmailContent = useMemo<EmailContentPreset | null>(() => {
    if (templateType !== TemplateType.EMAIL) return null;
    if (selectedContentKey === BLANK_KEY) return null;
    return (
      EMAIL_CONTENT_PRESETS.find((p) => p.key === selectedContentKey) ?? null
    );
  }, [selectedContentKey, templateType]);

  // The composed preview {subject, text, style} when both content + style
  // are picked. Null when no content is selected (callers fall back to the
  // style's own default text) or when the style isn't letter-compatible
  // (callers show a blocking hint).
  const composedEmail = useMemo(() => {
    if (templateType !== TemplateType.EMAIL) return null;
    if (!selectedEmailContent) return null;
    if (selectedEmailDefault) {
      if (!isLetterCompatible(selectedEmailDefault.key)) return null;
      return composeEmail(selectedEmailDefault, selectedEmailContent);
    }
    // Content selected, style is Blank — fall back to Plain Letter so the
    // operator still gets a usable starting point.
    return composeWithPlainLetter(selectedEmailContent);
  }, [selectedEmailContent, selectedEmailDefault, templateType]);

  const styleIncompatibleWithContent =
    templateType === TemplateType.EMAIL &&
    !!selectedEmailContent &&
    !!selectedEmailDefault &&
    !isLetterCompatible(selectedEmailDefault.key);

  const selectedInAppDefault = useMemo<InAppDefault | null>(() => {
    if (templateType !== TemplateType.IN_APP_MESSAGE) return null;
    if (selectedDefaultKey === BLANK_KEY) return null;
    return IN_APP_DEFAULTS.find((d) => d.key === selectedDefaultKey) ?? null;
  }, [selectedDefaultKey, templateType]);

  const selectedPushDefault = useMemo<PushDefault | null>(() => {
    if (templateType !== TemplateType.PUSH) return null;
    if (selectedDefaultKey === BLANK_KEY) return null;
    return PUSH_DEFAULTS.find((d) => d.key === selectedDefaultKey) ?? null;
  }, [selectedDefaultKey, templateType]);

  const hasDefaultsForType =
    templateType === TemplateType.EMAIL ||
    templateType === TemplateType.IN_APP_MESSAGE ||
    templateType === TemplateType.PUSH;

  const goNext = () => {
    if (!templateName.trim()) return;
    setStep("starting-point");
  };

  const goBack = () => setStep("basics");

  const buildPayload = () => {
    const base: Record<string, unknown> = {
      name: templateName,
      type: templateType,
    };
    if (templateType === TemplateType.IN_APP_MESSAGE) {
      base.inAppState = selectedInAppDefault?.state ?? defaultInAppState;
    }
    if (templateType === TemplateType.PUSH && selectedPushDefault) {
      base.pushObject = selectedPushDefault.data;
    }
    if (templateType === TemplateType.EMAIL) {
      // Preference order:
      //   1. composed (content selected + letter-compatible style, or content
      //      selected with Blank style which falls back to Plain Letter)
      //   2. style-only (no content selected, style picked)
      //   3. blank (nothing selected)
      if (composedEmail) {
        base.subject = composedEmail.subject;
        base.text = composedEmail.text;
        if (composedEmail.style) base.style = composedEmail.style;
      } else if (selectedEmailDefault && selectedDefaultKey !== BLANK_KEY) {
        if (selectedEmailDefault.subject)
          base.subject = selectedEmailDefault.subject;
        base.text = selectedEmailDefault.text;
        if (selectedEmailDefault.style) base.style = selectedEmailDefault.style;
      }
    }
    return base;
  };

  const handleCreate = async () => {
    if (!templateName.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const { data } = await ApiService.post<{ id: number }>({
        url: ApiConfig.createTemplate,
        options: buildPayload(),
      });
      window.dispatchEvent(new CustomEvent("leadstats:templates-changed"));
      navigate(`/templates/${templateType}/${data.id}`);
      onClose();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message?.[0] ||
          e?.response?.data?.message ||
          "Failed to create template"
      );
      setIsSubmitting(false);
    }
  };

  const stepLabel = step === "basics" ? "Step 1 of 2" : "Step 2 of 2";

  const renderBasicsStep = () => (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <div className="text-[12px] font-semibold">Template name</div>
        <Input
          value={templateName}
          onChange={setTemplateName}
          placeholder="Enter a name"
          wrapperClassName="w-full"
          className="w-full"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="text-[12px] font-semibold">Template type</div>
        <div className="grid grid-cols-2 gap-3">
          {TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setTemplateType(opt.key)}
              className={`text-left p-4 rounded-md border transition-colors flex flex-col gap-2 ${
                templateType === opt.key
                  ? "border-[#6366F1] bg-themed-background"
                  : "border-themed-border hover:border-[#6366F1] hover:bg-themed-background"
              }`}
            >
              <div className="font-semibold text-[14px]">{opt.label}</div>
              <div className="text-[12px] text-themed-textSecondary">
                {opt.description}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderFavoriteStar = (
    favType: FavoriteTemplateStyleType,
    itemKey: string
  ) => {
    const isFav = favorites[favType] === itemKey;
    return (
      <span
        role="button"
        tabIndex={0}
        aria-label={isFav ? "Unfavorite this style" : "Favorite this style"}
        title={
          isFav
            ? "This is your default. Click to remove."
            : "Set as default for all template pickers"
        }
        onClick={async (e) => {
          e.stopPropagation();
          e.preventDefault();
          try {
            await setFavorite(favType, isFav ? null : itemKey);
            toast.success(
              isFav ? "Removed from your defaults" : "Saved as your default"
            );
          } catch {
            toast.error("Couldn't save your default");
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            (e.currentTarget as HTMLElement).click();
          }
        }}
        className="absolute top-1.5 right-1.5 flex items-center justify-center w-[20px] h-[20px] rounded hover:bg-themed-card cursor-pointer"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill={isFav ? "#F59E0B" : "none"}
          stroke={isFav ? "#F59E0B" : "currentColor"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      </span>
    );
  };

  const renderCardListAndPreview = (
    items: {
      key: string;
      name: string;
      description: string;
      category: string;
    }[],
    grouped: [string, typeof items][],
    blankDescription: string,
    previewNode: React.ReactNode,
    favType: FavoriteTemplateStyleType | null
  ) => (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: "280px 1fr", height: "70vh" }}
    >
      <div className="flex flex-col gap-3 overflow-y-auto pr-1">
        <button
          onClick={() => setSelectedDefaultKey(BLANK_KEY)}
          className={`text-left p-3 rounded-md border transition-colors flex flex-col gap-1 ${
            selectedDefaultKey === BLANK_KEY
              ? "border-[#6366F1] bg-themed-background"
              : "border-themed-border hover:bg-themed-background"
          }`}
        >
          <div className="font-semibold text-[13px]">Blank</div>
          <div className="text-[11px] text-themed-textSecondary">
            {blankDescription}
          </div>
        </button>
        {grouped.map(([category, groupItems]) => (
          <div key={category} className="flex flex-col gap-1.5">
            <div className="text-[10px] uppercase tracking-wide text-themed-textSecondary px-1">
              {category}
            </div>
            {groupItems.map((d) => (
              <button
                key={d.key}
                onClick={() => setSelectedDefaultKey(d.key)}
                className={`relative text-left p-3 pr-8 rounded-md border transition-colors flex flex-col gap-1 ${
                  selectedDefaultKey === d.key
                    ? "border-[#6366F1] bg-themed-background"
                    : "border-themed-border hover:bg-themed-background"
                }`}
              >
                <div className="font-semibold text-[13px]">{d.name}</div>
                <div className="text-[11px] text-themed-textSecondary">
                  {d.description}
                </div>
                {favType && renderFavoriteStar(favType, d.key)}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="min-h-0">{previewNode}</div>
    </div>
  );

  const renderBlankPreview = () => (
    <div className="flex h-full items-center justify-center border border-themed-border rounded bg-themed-background">
      <div className="text-[13px] text-themed-textSecondary italic">
        You'll start with an empty {typeLabel(templateType)}.
      </div>
    </div>
  );

  const renderEmailStartingPoint = () => {
    const renderListPanel = (
      title: string,
      blankLabel: string,
      blankDescription: string,
      selectedKey: string,
      onSelect: (key: string) => void,
      grouped: {
        category: string;
        name: string;
        description: string;
        key: string;
        meta?: string;
      }[][],
      isDisabled?: (key: string) => boolean,
      disabledHint?: string,
      favType?: FavoriteTemplateStyleType | null
    ) => (
      <div className="flex flex-col gap-2 min-h-0">
        <div className="text-[11px] uppercase tracking-wide text-themed-textSecondary font-semibold px-1">
          {title}
        </div>
        <div className="flex flex-col gap-2 overflow-y-auto pr-1">
          <button
            onClick={() => onSelect(BLANK_KEY)}
            className={`text-left p-2.5 rounded-md border transition-colors flex flex-col gap-0.5 ${
              selectedKey === BLANK_KEY
                ? "border-[#6366F1] bg-themed-background"
                : "border-themed-border hover:bg-themed-background"
            }`}
          >
            <div className="font-semibold text-[12px]">{blankLabel}</div>
            <div className="text-[10px] text-themed-textSecondary">
              {blankDescription}
            </div>
          </button>
          {grouped.map((items) => (
            <div key={items[0].category} className="flex flex-col gap-1">
              <div className="text-[9px] uppercase tracking-wide text-themed-textSecondary px-1 pt-1">
                {items[0].category}
              </div>
              {items.map((d) => {
                const disabled = isDisabled?.(d.key) ?? false;
                return (
                  <button
                    key={d.key}
                    onClick={() => !disabled && onSelect(d.key)}
                    title={disabled ? disabledHint : undefined}
                    disabled={disabled}
                    className={`relative text-left p-2.5 ${
                      favType ? "pr-8" : ""
                    } rounded-md border transition-colors flex flex-col gap-0.5 ${
                      selectedKey === d.key
                        ? "border-[#6366F1] bg-themed-background"
                        : disabled
                        ? "border-themed-border opacity-40 cursor-not-allowed"
                        : "border-themed-border hover:bg-themed-background"
                    }`}
                  >
                    <div className="font-semibold text-[12px]">{d.name}</div>
                    <div className="text-[10px] text-themed-textSecondary line-clamp-2">
                      {d.description}
                    </div>
                    {d.meta && (
                      <div className="text-[10px] text-[#6366F1] mt-0.5">
                        {d.meta}
                      </div>
                    )}
                    {favType && renderFavoriteStar(favType, d.key)}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );

    const contentItemsByCategory = groupedEmailContent.map(
      ([category, items]) =>
        items.map((p) => ({
          key: p.key,
          category,
          name: p.name,
          description: p.description,
          meta: p.cadenceLabel,
        }))
    );

    const styleItemsByCategory = groupedEmailDefaults.map(([category, items]) =>
      items.map((d) => ({
        key: d.key,
        category,
        name: d.name,
        description: d.description,
      }))
    );

    // Build the preview node.
    let previewNode: React.ReactNode;
    if (styleIncompatibleWithContent) {
      previewNode = (
        <div className="flex h-full items-center justify-center border border-themed-border rounded bg-themed-background px-6">
          <div className="text-[13px] text-themed-textSecondary text-center max-w-[320px]">
            The{" "}
            <span className="font-semibold">{selectedEmailDefault?.name}</span>{" "}
            style is a structural layout (it doesn't have a paragraph body
            slot), so it can't host the selected content. Pick a different style
            — or leave style blank to use Plain Letter.
          </div>
        </div>
      );
    } else if (composedEmail) {
      previewNode = (
        <EmailPreview
          subject={composedEmail.subject}
          text={composedEmail.text}
        />
      );
    } else if (selectedEmailDefault) {
      previewNode = (
        <EmailPreview
          subject={selectedEmailDefault.subject}
          text={selectedEmailDefault.text}
        />
      );
    } else {
      previewNode = renderBlankPreview();
    }

    return (
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "220px 220px 1fr", height: "70vh" }}
      >
        {renderListPanel(
          "Content",
          "Blank content",
          "Write the body from scratch.",
          selectedContentKey,
          setSelectedContentKey,
          contentItemsByCategory
        )}
        {renderListPanel(
          "Style",
          "Blank style",
          selectedEmailContent
            ? "Use the Plain Letter style."
            : "Start with an empty email.",
          selectedDefaultKey,
          setSelectedDefaultKey,
          styleItemsByCategory,
          (key) => !!selectedEmailContent && !isLetterCompatible(key),
          "This style is structural and can't host custom paragraph content. Clear the content selection to use it.",
          "email"
        )}
        <div className="min-h-0">{previewNode}</div>
      </div>
    );
  };

  const renderInAppStartingPoint = () =>
    renderCardListAndPreview(
      IN_APP_DEFAULTS,
      groupedInAppDefaults,
      "Start with an empty in-app modal.",
      selectedInAppDefault ? (
        <InAppPreview state={selectedInAppDefault.state} />
      ) : (
        renderBlankPreview()
      ),
      "inApp"
    );

  const renderPushStartingPoint = () =>
    renderCardListAndPreview(
      PUSH_DEFAULTS,
      groupedPushDefaults,
      "Start with an empty push notification.",
      selectedPushDefault ? (
        <PushPreview data={selectedPushDefault.data} />
      ) : (
        renderBlankPreview()
      ),
      "push"
    );

  const renderEmptyStartingPoint = () => (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: "280px 1fr", height: "70vh" }}
    >
      <div className="flex flex-col gap-3 overflow-y-auto pr-1">
        <button
          className="text-left p-3 rounded-md border border-[#6366F1] bg-themed-background flex flex-col gap-1"
          disabled
        >
          <div className="font-semibold text-[13px]">Blank</div>
          <div className="text-[11px] text-themed-textSecondary">
            Start with an empty {typeLabel(templateType)}.
          </div>
        </button>
        <div className="text-[11px] text-themed-textSecondary px-1 pt-2 italic">
          No starting points available yet for {typeLabel(templateType)}.
        </div>
      </div>
      {renderBlankPreview()}
    </div>
  );

  const renderStartingPointStep = () => {
    if (templateType === TemplateType.EMAIL) return renderEmailStartingPoint();
    if (templateType === TemplateType.IN_APP_MESSAGE)
      return renderInAppStartingPoint();
    if (templateType === TemplateType.PUSH) return renderPushStartingPoint();
    return renderEmptyStartingPoint();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      panelClassName="w-[1180px] max-w-[95vw] max-h-[92vh] px-[32px] pt-[32px] pb-[24px] flex flex-col"
    >
      <div className="flex flex-col gap-5 font-inter text-[14px] text-themed-text">
        <div className="flex items-center justify-between">
          <div className="text-[20px] font-semibold leading-[28px]">
            Create a template
          </div>
          <div className="text-[12px] text-themed-textSecondary">
            {stepLabel}
          </div>
        </div>

        {step === "basics" && renderBasicsStep()}
        {step === "starting-point" && renderStartingPointStep()}

        <div className="flex justify-end gap-2 pt-2 border-t border-themed-border">
          {step === "basics" && (
            <>
              <Button type={ButtonType.SECONDARY} onClick={onClose}>
                Cancel
              </Button>
              <Button
                type={ButtonType.PRIMARY}
                onClick={goNext}
                disabled={!templateName.trim()}
              >
                Next
              </Button>
            </>
          )}
          {step === "starting-point" && (
            <>
              <Button
                type={ButtonType.SECONDARY}
                onClick={goBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                type={ButtonType.PRIMARY}
                onClick={handleCreate}
                disabled={
                  !templateName.trim() ||
                  isSubmitting ||
                  styleIncompatibleWithContent
                }
              >
                {isSubmitting ? "Creating…" : "Create"}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CreateTemplateWizardModal;
